import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useCan } from '@features/user';
import { Stack, Typography } from '@mui/material';
import { FeatureGates, useEventTracking, useFeatureGate } from '@services/statsig';

import { AvailabilitySection } from './AvailabilitySection';
import { BookingGatesFunnelSection } from './BookingGatesFunnelSection';
import { ConfirmationModeSection } from './ConfirmationModeSection';
import { ManagePolicySection } from './ManagePolicySection';

/** Practice settings page for self-serve scheduling: availability + booking
 * gates. Rendered inside PracticeLayout at /practice/scheduling-settings.
 *
 * Guarded on both axes independently of PracticeLayout's outer check. That check
 * now admits calendar managers too, but it also admits billing/org/appointment-
 * types users who must not reach this page — so we re-verify here:
 * - `manage_org_calendar` — the permission the tab and the backend gate/
 *   availability write APIs require; blocks direct-URL access for users without
 *   it (e.g. a supervisor).
 * - PATIENT_SELF_SCHEDULING feature gate — hides the whole surface until rollout
 *   (the tab and nav entry are also gated on it). */
export function SchedulingSettingsPage() {
  const { schedulingSettings } = useEventTracking();
  const canManageScheduling = useCan('manage_org_calendar');
  const schedulingFeatureEnabled = useFeatureGate(
    FeatureGates.PATIENT_SELF_SCHEDULING,
  );
  const canView = canManageScheduling && schedulingFeatureEnabled;

  useEffect(() => {
    if (canView) {
      schedulingSettings.opened();
    }
  }, [schedulingSettings, canView]);

  if (!canView) {
    return <Navigate to="/practice" replace />;
  }

  return (
    <Stack spacing={3} sx={{ maxWidth: 920, mx: 'auto', width: '100%', py: 1 }}>
      <div>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Scheduling
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure self-serve booking — your availability and the gates that
          screen who can land on your calendar.
        </Typography>
      </div>
      <AvailabilitySection />
      <BookingGatesFunnelSection />
      <ConfirmationModeSection />
      <ManagePolicySection />
    </Stack>
  );
}
